import React, { useState, useEffect } from 'react';

import { 
  Box, 
  Typography, 
 
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Alert,
  Button,
  Fab,
  Collapse,
  IconButton,
  Divider,
  Chip
} from '@mui/material';
import useAppNavigation from '../hooks/useAppNavigation';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ShareIcon from '@mui/icons-material/Share';
import Layout from '../components/Layout';
import LoadingOverlay from '../components/LoadingOverlay';
import BoxThumbnail from '../components/BoxThumbnail';
import api, { directApi } from '../services/api';
import offlineApi from '../services/offlineApi';

import boxService from '../services/boxService';
import familyService from '../services/familyService';
import demoService from '../services/demoService';

import EnhancedSearchBar from '../components/EnhancedSearchBar';

// Choose the appropriate API instance based on environment
const apiInstance = process.env.REACT_APP_API_URL ? directApi : api;

const AllBoxes = () => {
  const navigate = useAppNavigation();
  const [boxes, setBoxes] = useState([]);
  const [filteredBoxes, setFilteredBoxes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sharedBoxes, setSharedBoxes] = useState([]);
  const [sharedBoxesExpanded, setSharedBoxesExpanded] = useState(true);
  const [loadingShared, setLoadingShared] = useState(false);
  // AI search related states
const [searchTier, setSearchTier] = useState('keyword');
const [searchTime, setSearchTime] = useState(null);
const [isSearching, setIsSearching] = useState(false);
  
  // Load shared boxes
  useEffect(() => {
    const loadSharedBoxes = async () => {
      try {
        setLoadingShared(true);
        const result = await familyService.getSharedBoxes();
        if (result.success) {
          setSharedBoxes(result.shared_boxes || []);
        }
      } catch (err) {
        console.error('Error loading shared boxes:', err);
      } finally {
        setLoadingShared(false);
      }
    };
    
    loadSharedBoxes();
  }, []);
  
  // Check if offline data is available
  useEffect(() => {
    const checkForOfflineData = async () => {
      const isActuallyOffline = !navigator.onLine;
      
      if (isActuallyOffline) {
        console.log('Device is offline - checking for available offline data...');
        await boxService.checkOfflineData();
      }
    };
    
    checkForOfflineData();
  }, []);
  
  // Mark explorer demo feature as completed when page loads
  useEffect(() => {
    if (demoService.isDemoMode()) {
      demoService.markFeatureCompleted('explorer');
    }
  }, []);

  // Fetch all boxes and their items from API or offline storage
  useEffect(() => {
    const fetchBoxes = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        console.log('Fetching boxes for All Boxes page...');
        
        // Check if offline mode is enabled
        const isOfflineMode = await offlineApi.isOfflineModeEnabled();
        const isActuallyOffline = !navigator.onLine;
        
        console.log(`Offline mode status - enabled: ${isOfflineMode}, actually offline: ${isActuallyOffline}`);
        
        console.log(`Using ${(isOfflineMode || isActuallyOffline) ? 'offline' : 'online'} API`);
        
        // First try to fetch boxes using the boxService
        let boxesArray = [];
        try {
          console.log('Fetching boxes with boxService...');
          const boxesData = await boxService.getAllBoxes();
          console.log('Boxes fetched successfully:', boxesData);
          
          // Ensure we have an array of boxes
          boxesArray = Array.isArray(boxesData) ? boxesData : [];
          console.log(`Received ${boxesArray.length} boxes from the service`);
        } catch (boxError) {
          console.error('Error fetching boxes with boxService:', boxError);
          // Continue with empty array, we'll try a different approach below
        }
        
        // If we didn't get boxes from the service, try a direct approach
        if (boxesArray.length === 0) {
          try {
            // Try the appropriate API for our connection status
            console.log(`Trying to fetch boxes directly with ${isOfflineMode || isActuallyOffline ? 'offline' : 'online'} API...`);
            
            if (isOfflineMode || isActuallyOffline) {
              // Device is offline, use the offlineApi
              const offlineBoxes = await offlineApi.getAllBoxes();
              console.log('Offline boxes:', offlineBoxes);
              boxesArray = Array.isArray(offlineBoxes) ? offlineBoxes : [];
            } else {
              // Fetch from server
              const boxesResponse = await apiInstance.get('/boxes');
              boxesArray = Array.isArray(boxesResponse?.data) ? boxesResponse.data : [];
            }
            
            console.log(`Received ${boxesArray.length} boxes directly`);
          } catch (directApiError) {
            console.error('Error fetching boxes directly:', directApiError);
          }
        }
        
        // Fetch all items to calculate accurate counts, using the appropriate API
        let itemsData = [];
        try {
          console.log(`Fetching all items for count calculation with ${isOfflineMode || isActuallyOffline ? 'offline' : 'online'} API...`);
          
          if (isOfflineMode || isActuallyOffline) {
            // Fetch from offline API
            const offlineItems = await offlineApi.getAllItems();
            console.log('Offline items:', offlineItems);
            itemsData = Array.isArray(offlineItems) ? offlineItems : [];
          } else {
            // Fetch from server
            const itemsResponse = await apiInstance.get('/items');
            itemsData = Array.isArray(itemsResponse?.data) ? itemsResponse.data : [];
          }
          
          console.log('Fetched items data, total:', itemsData.length);
        } catch (itemError) {
          console.error('Error fetching items:', itemError);
          // Continue with empty items array
        }
        
        // Calculate accurate item counts for each box
        const boxesWithCounts = boxesArray.map(box => {
          if (!box || typeof box !== 'object') {
            console.warn('Invalid box object in boxesArray:', box);
            return null; // Will be filtered out below
          }
          
          // Ensure box has an id
          const boxId = box.id;
          if (boxId === undefined || boxId === null) {
            console.warn('Box missing ID:', box);
            return null; // Will be filtered out below
          }
          
          // Count items that belong to this box
          const boxItems = itemsData.filter(item => 
            item && item.box_id !== undefined && item.box_id === boxId
          );
          
          console.log(`Box ${boxId} (${box.name || 'Unnamed'}) has ${boxItems.length} items`);
          
          return {
            ...box,
            items: boxItems, // Include the actual items
            items_count: boxItems.length,
            item_count: boxItems.length
          };
        }).filter(box => box !== null); // Remove any invalid boxes

        // Process boxes to ensure consistent structure
        
        // Use boxesWithCounts which already has the item counts correctly set
        if (boxesWithCounts.length > 0) {
          console.log('Boxes with counts:', boxesWithCounts);
          setBoxes(boxesWithCounts);
          setFilteredBoxes(boxesWithCounts);
        } else {
          setBoxes([]);
          setFilteredBoxes([]);
        }
      } catch (error) {
        console.error('Error fetching boxes:', error);
        setError('Failed to load boxes. Please try again.');
        setBoxes([]);
        setFilteredBoxes([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBoxes();
  }, []);

  // Listen for data refresh events (items added/deleted)
  useEffect(() => {
    const checkForDataRefresh = () => {
      // Check if items were added (e.g., from multi-item image cropping)
      const itemsAdded = localStorage.getItem('cubxly_items_added');
      const forceRefresh = localStorage.getItem('cubxly_force_refresh');
      
      if (itemsAdded || forceRefresh) {
        console.log('Items were added or force refresh triggered - reloading boxes...');
        // Clear the signals
        localStorage.removeItem('cubxly_items_added');
        localStorage.removeItem('cubxly_box_updated');
        localStorage.removeItem('cubxly_force_refresh');
        // Trigger a re-render by updating a state that causes the fetch useEffect to run
        setBoxes([]);
        setFilteredBoxes([]);
        setIsLoading(true);
        return true;
      }
      return false;
    };

    // Check on visibility change (when user returns to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (checkForDataRefresh()) {
          // Force re-fetch by triggering the useEffect
          window.location.reload();
        }
      }
    };

    // Check on focus
    const handleFocus = () => {
      if (checkForDataRefresh()) {
        window.location.reload();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Initial check
    checkForDataRefresh();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Filter boxes based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredBoxes(boxes);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = boxes.filter(box => {
      // Search through name, description, and location
      return (
        (box.name && box.name.toLowerCase().includes(query)) ||
        (box.description && box.description.toLowerCase().includes(query)) ||
        (box.location_full && box.location_full.toLowerCase().includes(query)) ||
        (box.location && box.location.toLowerCase().includes(query))
      );
    });

    setFilteredBoxes(filtered);
  }, [searchQuery, boxes]);

  // Handle search form submission
  const handleSearchSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    if (!searchQuery.trim()) {
      setFilteredBoxes(boxes);
      return;
    }
    
    try {
      setIsSearching(true);
      const startTime = performance.now();
      
      // First try to use the backend search API
      try {
        const response = await apiInstance.get(`/search/boxes?q=${encodeURIComponent(searchQuery)}`);
        
        if (response.data && Array.isArray(response.data)) {
          console.log('Search API returned boxes:', response.data);
          setFilteredBoxes(response.data);
          
          // Check if API indicates which search tier was used
          if (response.headers && response.headers['x-search-tier']) {
            setSearchTier(response.headers['x-search-tier']);
          }
          
          const endTime = performance.now();
          setSearchTime(Math.round(endTime - startTime));
          setIsSearching(false);
          return;
        }
      } catch (apiError) {
        console.warn('API search failed, falling back to local search:', apiError);
      }
      
      // Fallback to local search if API fails
      console.log('Using local search for boxes');
      setSearchTier('keyword'); // Local search is always keyword-based
      const query = searchQuery.toLowerCase();
      
      // Filter boxes that match the query in name, description, or location
      const filtered = boxes.filter(box => {
        return (
          (box.name && box.name.toLowerCase().includes(query)) ||
          (box.description && box.description.toLowerCase().includes(query)) ||
          (box.location_full && box.location_full.toLowerCase().includes(query)) ||
          (box.location && box.location.toLowerCase().includes(query))
        );
      });
      
      setFilteredBoxes(filtered);
      const endTime = performance.now();
      setSearchTime(Math.round(endTime - startTime));
    } catch (error) {
      console.error('Search error:', error);
      setError('Failed to search. Please try again.');
      setSearchTier('keyword');
      setSearchTime(null);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    
    // If search is empty, reset to show all boxes
    if (!value.trim()) {
      setFilteredBoxes(boxes);
      setSearchTier('keyword');
      setSearchTime(null);
    }
  };
// Create the search component for the header
const headerSearchComponent = (
  <Box sx={{ flexGrow: 1, mx: 1 }}>
    <EnhancedSearchBar
      value={searchQuery}
      onChange={handleSearchChange}
      onSubmit={handleSearchSubmit}
      searchTier={searchTier}
      searchTime={searchTime}
      isSearching={isSearching}
      placeholder="Search boxes..."
      elevation={0}
      sx={{ 
        backgroundColor: 'white',
        borderRadius: 1,
        border: '2px solid rgba(0, 0, 0, 0.1)',
        p: 0.5,
        '& .MuiInputBase-root': {
          color: 'rgba(0, 0, 0, 0.87)',
          height: 38,
          pl: 1
        },
        '&:hover': {
          border: '2px solid rgba(0, 0, 0, 0.23)'
        },
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
      }}
    />
  </Box>
);

  return (
    <Layout 
      title="All Boxes" 
      onBack={() => navigate.to.HOME()} 
      searchComponent={headerSearchComponent}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <LoadingOverlay />
      ) : (
        <>
        {/* Shared Boxes Section */}
        {sharedBoxes.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mb: 2,
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 }
              }}
              onClick={() => setSharedBoxesExpanded(!sharedBoxesExpanded)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShareIcon color="primary" />
                <Typography variant="h6">
                  Shared With You
                </Typography>
                <Chip 
                  label={sharedBoxes.length} 
                  size="small" 
                  color="primary"
                />
              </Box>
              <IconButton size="small">
                {sharedBoxesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            
            <Collapse in={sharedBoxesExpanded}>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {sharedBoxes.map((box) => (
                  <Grid item xs={12} sm={6} md={4} key={box.id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', border: '2px solid', borderColor: 'primary.light' }}>
                      <CardActionArea 
                        onClick={() => navigate.to.BOX_DETAIL(box.id)}
                        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', height: '100%' }}
                      >
                        <BoxThumbnail boxId={box.id} height={100} />
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <ShareIcon fontSize="small" color="primary" />
                            <Chip 
                              label={box.permission_level === 'view' ? 'View Only' : 'Can Edit'} 
                              size="small" 
                              color="primary"
                              variant="outlined"
                            />
                          </Box>
                          <Typography gutterBottom variant="h6" component="div">
                            {box.name}
                          </Typography>
                          {(box.location_full || box.location) && (
                            <Typography variant="body2" color="text.secondary">
                              📍 {box.location_full || box.location}
                            </Typography>
                          )}
                          {box.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {box.description.length > 70 
                                ? box.description.substring(0, 70) + '...'
                                : box.description}
                            </Typography>
                          )}
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Collapse>
            
            <Divider sx={{ my: 3 }} />
          </Box>
        )}
        
        {/* My Boxes Section */}
        <Typography variant="h6" sx={{ mb: 2 }}>
          My Boxes
        </Typography>
        
<Grid container spacing={2}>
  {filteredBoxes.length > 0 ? (
    filteredBoxes.map((box) => (
      <Grid item xs={12} sm={6} md={4} key={box.id}>
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <CardActionArea 
            onClick={() => navigate.to.BOX_DETAIL(box.id)}
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', height: '100%' }}
          >
            <BoxThumbnail boxId={box.id} height={100} />
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography gutterBottom variant="h6" component="div">
                {box.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {(() => {
                  // First check presence of the items array
                  if (box.items && Array.isArray(box.items)) {
                    return `${box.items.length} item${box.items.length !== 1 ? 's' : ''}`;
                  }
                  // Then check explicit counts
                  else if (typeof box.items_count === 'number') {
                    return `${box.items_count} item${box.items_count !== 1 ? 's' : ''}`;
                  }
                  else if (typeof box.item_count === 'number') {
                    return `${box.item_count} item${box.item_count !== 1 ? 's' : ''}`;
                  }
                  // Last resort - if no data is available
                  else {
                    return '0 items';
                  }
                })()}
              </Typography>
              {(box.location_full || box.location) && (
                <Typography variant="body2" color="text.secondary">
                  📍 {box.location_full || box.location}
                </Typography>
              )}
              {box.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {box.description.length > 70 
                    ? box.description.substring(0, 70) + '...'
                    : box.description}
                </Typography>
              )}
            </CardContent>
          </CardActionArea>
        </Card>
      </Grid>
    ))
  ) : (
    <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        {searchQuery ? 'No boxes match your search' : 'No boxes found'}
      </Typography>
      {!searchQuery && (
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate.to.CREATE_BOX()}
          sx={{ mt: 2 }}
        >
          Create New Box
        </Button>
      )}
    </Box>
  )}
</Grid>
        </>
      )}
      
      {/* Floating Action Button to create box */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 76, right: 16 }} /* Adjusted to account for bottom nav */
        onClick={() => navigate.to.CREATE_BOX()}
      >
        <AddIcon />
      </Fab>
    </Layout>
  );
};

export default AllBoxes;
